import { markComplianceItemAsExtended } from '@commenda/api';
import {
  Button,
  DateField,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  LoadingIndicator,
  SingleFileUploader,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_SOURCE,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { LegacyDocumentType } from '@commenda/prisma';
import { FileBasic } from '@commenda/types/src/file/types';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { useState } from 'react';
import { useNotification } from '../../../../hooks/useNotification';

interface ExtendFilingModalProps {
  isOpen: boolean;
  onClose: () => void;
  filingId: string;
  filingTitle: string;
  currentDueDate?: string;
  entityName?: string;
  companyId: number;
  onSuccess: () => void;
}

const ExtendFilingModal = ({
  isOpen,
  onClose,
  filingId,
  filingTitle,
  currentDueDate,
  entityName,
  companyId,
  onSuccess,
}: ExtendFilingModalProps) => {
  const { setNotification } = useNotification();

  const [newDueDate, setNewDueDate] = useState<string | undefined>(undefined);
  const [note, setNote] = useState('');
  const [file, setFile] = useState<FileBasic | null>(null);

  const isSubmitting = useToggle(false);

  // The new due date must move the deadline forward. Server enforces this too, but
  // disabling past/equal dates in the picker gives the user immediate feedback.
  const isDateInvalid = (() => {
    if (!newDueDate) return true;
    if (!currentDueDate) return false;
    return NonTimezoneSensitiveDate.fromISO8601String(newDueDate).lte(
      NonTimezoneSensitiveDate.fromISO8601String(currentDueDate)
    );
  })();

  const reset = () => {
    setNewDueDate(undefined);
    setNote('');
    setFile(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const onExtend = async () => {
    if (!newDueDate || isDateInvalid) return;

    isSubmitting.on();

    const res = await markComplianceItemAsExtended(filingId, {
      newDueDate,
      reasonForExtension: note.trim() || undefined,
      extensionAcknowledgementFile: file ?? undefined,
    });

    if (!res.success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to extend filing.',
      });
      isSubmitting.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Filing due date extended.',
    });

    mixpanel.track('CALENDAR_FILING_EXTENDED', {
      source: MIXPANEL_SOURCE.CALENDAR,
      surface_area: MIXPANEL_SURFACE_AREA.COMPLIANCE_CALENDAR,
      compliance_id: filingId,
      has_note: note.trim().length > 0,
      has_acknowledgement: file !== null,
    });

    await onSuccess();
    isSubmitting.off();
    close();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col gap-xs items-start border-b-0 pb-0">
          <DialogTitle>Extend filing due date</DialogTitle>
          <DialogDescription>
            Update the filing deadline for{' '}
            <span className="font-bold">{filingTitle}</span>
            {entityName ? <> for {entityName}</> : null}.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-xl p-3xl">
          <FieldWrapper>
            <Label className="flex items-center gap-xxs">
              <span>Extended due date</span>
              <span className="text-utility-violet-600">*</span>
            </Label>
            <DateField
              value={newDueDate}
              onChange={setNewDueDate}
              displayFormat="in"
              fullWidth
              error={
                Boolean(newDueDate) && isDateInvalid
                  ? 'New due date must be after the current due date.'
                  : undefined
              }
            />
          </FieldWrapper>
          <FieldWrapper>
            <Label className="flex items-center gap-xxs">
              <span>Note</span>
              <span className="text-text-quaternary">(optional)</span>
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Type here..."
              maxLength={2000}
            />
          </FieldWrapper>
          <FieldWrapper>
            <Label className="flex items-center gap-xxs">
              <span>Upload filing extension document</span>
              <span className="text-text-quaternary">(optional)</span>
            </Label>
            <SingleFileUploader
              companyId={companyId}
              documentType={LegacyDocumentType.Other}
              file={file}
              setFile={setFile}
            />
          </FieldWrapper>
        </div>
        <DialogFooter className="border-t-0">
          <Button variant="secondary" onClick={close} size="md">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onExtend}
            size="md"
            disabled={!newDueDate || isDateInvalid || isSubmitting.value}
          >
            {isSubmitting.value ? <LoadingIndicator size={16} /> : null}
            <span>Extend due date</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExtendFilingModal;
